import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import api from "../lib/api";
import { UploadSimple, FileArrowUp, X } from "@phosphor-icons/react";
import { Section, DeviceFormSections, DEVICE_DEFAULTS, inputCls, labelCls } from "../components/DeviceFormSections";

const NORM = { imei: "imei", vehicle_number: "vehicle_number", vehiclenumber: "vehicle_number", vehicle: "vehicle_number", vehicle_type: "vehicle_type", type: "vehicle_type", driver_name: "driver_name", driver: "driver_name", operator: "operator", sim: "operator" };
const BLANK = { imei: "", vehicle_number: "", dealer_id: "", ...DEVICE_DEFAULTS };
const BLANK_CUST = { name: "", company: "", mobile: "", email: "", password: "" };

export default function AddDevice() {
  const [customers, setCustomers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [custMode, setCustMode] = useState("existing");
  const [customerId, setCustomerId] = useState("");
  const [newCust, setNewCust] = useState({ ...BLANK_CUST });
  const [f, setF] = useState({ ...BLANK });
  const [busy, setBusy] = useState(false);

  const [fileRows, setFileRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const loadCustomers = () => api.get("/customers").then((r) => {
    setCustomers(r.data.customers);
    if (!r.data.customers.length) setCustMode("new");
  }).catch(() => {});
  useEffect(() => {
    loadCustomers();
    api.get("/dealers").then((r) => setDealers(r.data.dealers || [])).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data.groups || [])).catch(() => {});
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setC = (k, v) => setNewCust((c) => ({ ...c, [k]: v }));
  const reset = () => { setF({ ...BLANK }); setNewCust({ ...BLANK_CUST }); setCustomerId(""); };

  const submit = async () => {
    if (!f.vehicle_number.trim()) return toast.error("Registration number is required");
    if (!f.imei.trim()) return toast.error("IMEI is required");
    const payload = { ...f };
    if (custMode === "existing") payload.customer_id = customerId || null;
    else if (newCust.name.trim()) payload.new_customer = newCust;
    setBusy(true);
    try {
      const r = await api.post("/provision", payload);
      toast.success(`Device registered — ${r.data.vehicle_number}`);
      reset(); loadCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to register device"); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      const parsed = raw.map((r) => {
        const o = {};
        Object.keys(r).forEach((k) => { const key = NORM[String(k).trim().toLowerCase().replace(/\s+/g, "_")]; if (key) o[key] = String(r[k]).trim(); });
        return o;
      }).filter((o) => o.imei && o.vehicle_number);
      if (!parsed.length) return toast.error("No valid rows. Need columns: imei, vehicle_number");
      setFileRows(parsed);
      toast.success(`${parsed.length} rows ready to import`);
    } catch { toast.error("Could not read file"); }
  };

  const importFleet = async () => {
    if (!fileRows.length) return toast.error("Upload a file first");
    setBusy(true);
    try {
      const body = { rows: fileRows };
      if (custMode === "existing" && customerId) body.customer_id = customerId;
      const r = await api.post("/provision/bulk", body);
      setResult(r.data); toast.success(`Imported ${r.data.created}/${r.data.total}`); loadCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "imei,vehicle_number,vehicle_type,driver_name,operator\n865412034567890,MH12AB1234,Truck,Rahul Sharma,Airtel\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "device-import-template.csv"; a.click();
    toast.success("Template downloaded");
  };

  return (
    <div className="p-5 max-w-6xl mx-auto space-y-4" data-testid="add-device-page">
      <div className="rounded-md overflow-hidden border border-border">
        <div className="px-6 py-5 bg-gradient-to-r from-[#2f4bff] to-[#4361ee] text-white">
          <div className="font-display font-black text-2xl">Add AIS-140 device</div>
          <div className="text-sm text-white/80">Complete assignment, SIM, license and operating settings.</div>
        </div>
      </div>

      <Section title="Device details" subtitle="Mandatory identity and ownership">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Registration number <span className="text-destructive">*</span></label>
            <input data-testid="dev-regno" value={f.vehicle_number} onChange={(e) => set("vehicle_number", e.target.value)} placeholder="e.g. MH 12 AB 1234" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>IMEI <span className="text-destructive">*</span></label>
            <input data-testid="dev-imei" value={f.imei} onChange={(e) => set("imei", e.target.value)} placeholder="15-digit device IMEI" className={`${inputCls} font-mono`} />
          </div>
          <div>
            <label className={labelCls}>Dealer</label>
            <select data-testid="dev-dealer" value={f.dealer_id} onChange={(e) => set("dealer_id", e.target.value)} className={inputCls}>
              <option value="">Unassigned</option>
              {dealers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Customer / user</label>
            {custMode === "existing" ? (
              <select data-testid="dev-customer" value={customerId} onChange={(e) => { if (e.target.value === "__new") setCustMode("new"); else setCustomerId(e.target.value); }} className={inputCls}>
                <option value="">Unassigned</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
                <option value="__new">＋ New customer…</option>
              </select>
            ) : (
              <div className="flex gap-1.5">
                <input data-testid="dev-newcust-name" value={newCust.name} onChange={(e) => setC("name", e.target.value)} placeholder="New customer name" className={inputCls} />
                <button onClick={() => { setCustMode("existing"); setNewCust({ ...BLANK_CUST }); }} className="px-2 border border-border rounded-md text-muted-foreground hover:text-destructive"><X size={16} /></button>
              </div>
            )}
          </div>
        </div>
        {custMode === "new" && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-border">
            <input value={newCust.company} onChange={(e) => setC("company", e.target.value)} placeholder="Company" className={inputCls} />
            <input value={newCust.mobile} onChange={(e) => setC("mobile", e.target.value)} placeholder="Mobile" className={inputCls} />
            <input value={newCust.email} onChange={(e) => setC("email", e.target.value)} placeholder="Login email (optional)" className={`${inputCls} font-mono`} />
            <input value={newCust.password} onChange={(e) => setC("password", e.target.value)} type="password" placeholder="Login password" className={inputCls} />
          </div>
        )}
      </Section>

      <DeviceFormSections f={f} set={set} groups={groups} />

      <div className="flex items-center justify-between bg-white border border-border rounded-md px-4 py-3 sticky bottom-3 shadow-sm">
        <span className="text-[11px] text-muted-foreground">Required fields are marked with <span className="text-destructive">*</span></span>
        <div className="flex gap-2">
          <button data-testid="dev-cancel" onClick={reset} className="px-5 py-2.5 text-sm font-semibold border border-border rounded-md hover:bg-black/5">Cancel</button>
          <button data-testid="dev-register" disabled={busy} onClick={submit} className="px-6 py-2.5 text-sm font-bold bg-primary text-white rounded-md disabled:opacity-50 hover:opacity-90">{busy ? "Registering…" : "Register device"}</button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-md p-4 space-y-2">
        <div className="flex items-center gap-1.5 font-bold text-sm"><UploadSimple size={16} /> Bulk Import (Excel / CSV)</div>
        <p className="text-xs text-muted-foreground">Upload a .xlsx or .csv. Columns: <code className="text-primary">imei, vehicle_number, vehicle_type, driver_name, operator</code>. {custMode === "existing" && customerId ? "Imported vehicles are assigned to the selected customer." : "Select an existing customer above to auto-assign imported vehicles."}</p>
        <button data-testid="bulk-template" onClick={downloadTemplate} className="text-xs font-semibold text-primary hover:underline">↓ Download CSV template</button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} data-testid="bulk-file" className="hidden" />
        <button data-testid="bulk-choose" onClick={() => fileRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 border border-dashed border-border rounded-md py-6 text-sm text-muted-foreground hover:border-primary hover:text-foreground">
          <FileArrowUp size={20} /> {fileName || "Choose spreadsheet file"}
        </button>
        {fileRows.length > 0 && <div className="text-xs text-muted-foreground">{fileRows.length} rows detected</div>}
        <button data-testid="bulk-import" disabled={busy || !fileRows.length || custMode === "new"} onClick={importFleet} className="bg-secondary text-white font-semibold text-sm px-6 py-2 rounded-md disabled:opacity-50">Import Fleet</button>
        {result && <div className="text-xs mt-2"><span className="text-[#34C759] font-semibold">{result.created} created</span> / {result.total} rows.
          {result.results.filter((r) => r.error).slice(0, 5).map((r, i) => <div key={i} className="text-destructive">{r.imei || "?"}: {r.error}</div>)}</div>}
      </div>
    </div>
  );
}
